import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, User, Calendar, Loader2, Filter, Search, Unlock, Lock, AlertTriangle, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Solicitudes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);

  const [filterFracc, setFilterFracc] = useState(() => localStorage.getItem('filtro_fraccionamiento') || 'todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!response.ok) { navigate('/login'); return; }
        setUser(await response.json());
      } catch { navigate('/login'); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filterFracc !== 'todos') params.append('fraccionamiento_id', filterFracc);
        if (filterTipo !== 'todos') params.append('tipo', filterTipo);
        const qs = params.toString() ? `?${params.toString()}` : '';

        const [histRes, fraccsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/lotes/historial${qs}`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
        ]);
        if (histRes.ok) setHistorial(await histRes.json());
        if (fraccsRes.ok) setFraccionamientos(await fraccsRes.json());
      } catch (e) {
        console.error('Error:', e);
      } finally { setLoading(false); }
    };
    fetchData();
  }, [user, filterFracc, filterTipo]);

  const isAdmin = user?.account_role === 'owner' || user?.account_role === 'admin' || user?.role === 'owner' || user?.role === 'admin';

  const filtered = useMemo(() => {
    if (!searchTerm) return historial;
    const term = searchTerm.toLowerCase();
    return historial.filter(h =>
      h.cliente_nombre?.toLowerCase().includes(term) ||
      h.usuario_nombre?.toLowerCase().includes(term) ||
      h.lote_numero?.toLowerCase().includes(term)
    );
  }, [historial, searchTerm]);

  const stats = useMemo(() => {
    const counts = { apartado: 0, liberado: 0, expirado: 0, convertido_venta: 0 };
    historial.forEach(h => { if (counts[h.tipo] !== undefined) counts[h.tipo]++; });
    return counts;
  }, [historial]);

  const getFraccName = (id) => fraccionamientos.find(f => f.fraccionamiento_id === id)?.nombre || '';

  const tipoConfig = {
    apartado: { icon: Lock, bg: 'bg-[#C6A54A]/15', text: 'text-[#C6A54A]', label: 'Apartado' },
    liberado: { icon: Unlock, bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Liberado' },
    expirado: { icon: AlertTriangle, bg: 'bg-red-500/15', text: 'text-red-400', label: 'Expirado' },
    convertido_venta: { icon: MapPin, bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Vendido' },
  };

  const getBadge = (tipo) => {
    const c = tipoConfig[tipo] || tipoConfig.apartado;
    const Icon = c.icon;
    return (
      <span data-testid={`tipo-badge-${tipo}`} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {c.label}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="historial-page" className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <History className="w-6 h-6 text-[#C6A54A]" />
          Historial de Apartados
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Registro de todos los apartados, liberaciones y expiraciones</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Apartados', value: stats.apartado, color: 'text-[#C6A54A]', bg: 'bg-[#C6A54A]/10' },
          { label: 'Liberados', value: stats.liberado, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Expirados', value: stats.expirado, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Total', value: historial.length, color: 'text-white', bg: 'bg-zinc-800' },
        ].map(s => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className={`${s.bg} rounded-xl p-4 border border-zinc-800/50`}>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900/50 rounded-xl p-3 border border-zinc-800/50">
        <Filter className="w-4 h-4 text-zinc-500 hidden sm:block" />
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger data-testid="filter-tipo" className="w-[150px] bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="todos" className="text-white">Todos</SelectItem>
            <SelectItem value="apartado" className="text-white">Apartados</SelectItem>
            <SelectItem value="liberado" className="text-white">Liberados</SelectItem>
            <SelectItem value="expirado" className="text-white">Expirados</SelectItem>
          </SelectContent>
        </Select>

        {isAdmin && (
          <Select value={filterFracc} onValueChange={(v) => { setFilterFracc(v); localStorage.setItem('filtro_fraccionamiento', v); }}>
            <SelectTrigger data-testid="filter-fraccionamiento" className="w-[200px] bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
              <SelectValue placeholder="Fraccionamiento" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="todos" className="text-white">Todos</SelectItem>
              {fraccionamientos.map(f => (
                <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white">{f.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="search-historial"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar cliente, lote..."
            className="pl-9 bg-zinc-800 border-zinc-700 text-white h-9 text-sm"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="empty-state" className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <History className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin registros</h3>
          <p className="text-sm text-zinc-500">El historial de apartados aparecerá aquí</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(h => (
            <div
              key={h.historial_id}
              data-testid={`historial-item-${h.historial_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex gap-3 items-start flex-1 min-w-0">
                  <div className="w-10 h-10 bg-[#C6A54A]/10 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#C6A54A]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-white text-sm">
                        Lote {h.lote_numero}{h.lote_manzana ? ` Mz ${h.lote_manzana}` : ''}
                      </h3>
                      <span className="text-xs text-zinc-500">{getFraccName(h.fraccionamiento_id)}</span>
                      {h.lote_estado_actual && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          h.lote_estado_actual === 'disponible' ? 'bg-amber-500/15 text-amber-400' :
                          h.lote_estado_actual === 'apartado' ? 'bg-[#C6A54A]/15 text-[#C6A54A]' :
                          'bg-zinc-600/15 text-zinc-400'
                        }`}>
                          Ahora: {h.lote_estado_actual}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-zinc-400">
                      {h.cliente_nombre && (
                        <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{h.cliente_nombre}</span>
                      )}
                      <span className="flex items-center gap-1 text-[#C6A54A]/80"><User className="w-3.5 h-3.5" />{h.usuario_nombre}</span>
                      {h.firma_programada && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Firma: {new Date(h.firma_programada).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} {new Date(h.firma_programada).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(h.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })} {new Date(h.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {h.motivo && <p className="text-xs text-zinc-500 mt-1 italic">Motivo: {h.motivo}</p>}
                  </div>
                </div>
                <div className="shrink-0">
                  {getBadge(h.tipo)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
